import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Permissions } from "../../../permissions";
import {
  useFetchCustomerByIdQuery,
  useUpdateVcConfigMutation,
} from "../../../services/baseQuery";
import { toast } from "react-hot-toast";
import { IconButton, Tabs, Tab, Box } from "@mui/material";
import { User, Settings, CreditCard, Clock, RotateCw } from "lucide-react";

// Tab components
import CustomerOverviewTab from "./Components/CustomerDetailsComponents/CustomerOverviewTab";
import CustomerSettingsTab from "./Components/CustomerDetailsComponents/CustomerSettingsTab";
import CustomerBillingTab from "./Components/CustomerDetailsComponents/CustomerBillingTab";
import CustomerWatchHistoryTab from "./Components/CustomerDetailsComponents/CustomerWatchHistoryTab";

const ViewCustomerDetails = () => {
  const { customerId } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch customer details
  const {
    data: fetchCustomerData,
    isLoading,
    error,
    refetch,
  } = useFetchCustomerByIdQuery(customerId);

  const [updateVcConfig, { isLoading: isUpdating }] =
    useUpdateVcConfigMutation();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      refetch().finally(() => {
        setIsRefreshing(false);
      });
    }, 500);
  };

  const handleSettingsUpdate = async newSettings => {
    try {
      await updateVcConfig({
        configId: fetchCustomerData.data.vcConfigSettings._id,
        settings: {
          creditLimit: newSettings.creditLimit,
          brandVisibility: {
            visibilityType: newSettings.brandVisibility.visibilityType,
            ...(newSettings.brandVisibility.visibilityType ===
              "USER_SPECIFIC" && {
              removedBrands: newSettings.brandVisibility.removedBrands,
            }),
          },
          orderValidation: {
            validationType: newSettings.orderValidation.validationType,
            ...(newSettings.orderValidation.validationType ===
              "USER_SPECIFIC" && {
              minOrderValue: newSettings.orderValidation.minOrderValue,
              minOrderQty: newSettings.orderValidation.minOrderQty,
              validationCondition:
                newSettings.orderValidation.validationCondition,
            }),
          },
        },
        additionalNotes: newSettings.additionalNotes,
        customerId,
      }).unwrap();

      toast.success("Settings updated successfully");
      refetch(); // Refresh the customer data
    } catch (error) {
      toast.error(error.data?.message || "Failed to update settings");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center font-outfit">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-red-500 font-outfit">
        {error.data?.error?.message || "Error loading customer details"}
      </div>
    );
  }

  return (
    <div className="bg-white font-outfit">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <h1 className="text-2xl font-semibold font-outfit text-gray-800">
          Customer Details
        </h1>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <IconButton
            onClick={handleRefresh}
            className="hover:bg-gray-100"
            disabled={isRefreshing}
            title="Refresh">
            <RotateCw
              size={20}
              className={`text-gray-600 ${isRefreshing ? "animate-spin" : ""}`}
            />
          </IconButton>
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{
            style: {
              backgroundColor: "#5652B0",
            },
          }}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              color: "#4B5563",
              minWidth: "auto",
              padding: "12px 16px",
              fontFamily: "Outfit",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
            },
            "& .Mui-selected": {
              color: "#5652B0",
              fontWeight: 600,
            },
          }}>
          <Tab
            icon={<User size={16} />}
            iconPosition="start"
            label="Overview"
          />
          <Tab
            icon={<Settings size={16} />}
            iconPosition="start"
            label="Settings"
          />
          {Permissions.find(p => p.includes("show_customerBilling")) && (
            <Tab
              icon={<CreditCard size={16} />}
              iconPosition="start"
              label="Billing"
            />
          )}
          <Tab
            icon={<Clock size={16} />}
            iconPosition="start"
            label="Watch History"
          />
        </Tabs>
      </Box>

      <div
        className="p-6 overflow-y-auto font-outfit"
        style={{ maxHeight: "calc(100vh - 240px)" }}>
        {activeTab === 0 && (
          <CustomerOverviewTab
            customerData={fetchCustomerData.data.customerStore}
            vcConfigSettings={fetchCustomerData.data.vcConfigSettings}
            refetch={refetch}
          />
        )}
        {activeTab === 1 && (
          <CustomerSettingsTab
            customerId={customerId}
            customerData={fetchCustomerData.data.customerStore}
            vcConfigSettings={fetchCustomerData.data.vcConfigSettings}
            onUpdate={handleSettingsUpdate}
            isUpdating={isUpdating}
          />
        )}
        {activeTab === 2 &&
          Permissions.find(p => p.includes("show_customerBilling")) && (
            <CustomerBillingTab customerId={customerId} />
          )}
        {activeTab ===
          (Permissions.find(p => p.includes("show_customerBilling"))
            ? 3
            : 2) && <CustomerWatchHistoryTab customerId={customerId} />}
      </div>
    </div>
  );
};

export default ViewCustomerDetails;
